(ns ui.components.tiptap.mention-suggestion
  (:require
   ["@tiptap/core" :refer [posToDOMRect]]
   ["@floating-ui/dom" :refer [computePosition flip shift]]
   ["react" :as React]
   ["react-dom" :as ReactDOM]
   [clojure.string :as string]
   [common.util :as cu]
   [ui.components.tiptap.mention-list :refer [mention-list]]
   [ui.dom-globals :as dg]
   [oops.core :as oops :refer [oget]]))

(defn- create-renderer
  "Minimal replacement for @tiptap/react ReactRenderer.
   Creates a detached div, renders the mention-list React component into it,
   and exposes .element, .ref, .updateProps, and .destroy."
  [component props]
  (let [element     (dg/create-element "div")
        ref-holder  #js {:current nil}
        render-fn   (fn [p]
                      (ReactDOM/render
                       (React/createElement component
                                            (js/Object.assign #js {:ref ref-holder} p))
                       element))
        renderer    #js {}]
    (render-fn props)
    (oops/oset! renderer "element" element)
    (oops/oset! renderer "ref" (.-current ref-holder))
    (oops/oset! renderer "updateProps" (fn [new-props]
                                         (render-fn new-props)
                                         (oops/oset! renderer "ref" (.-current ref-holder))))
    (oops/oset! renderer "destroy" (fn []
                                     (ReactDOM/unmountComponentAtNode element)))
    renderer))

(defn- set-mention-style! [element]
  (dg/set-style! element "position" "absolute")
  (dg/set-style! element "width" "max-content")
  (dg/set-style! element "background-color" "white")
  (dg/set-style! element "z-index" "9999"))

(defn- update-position [editor element]
  (try
    (let [view            (oget editor "view")
          state           (oget view "state")
          from            (oget state "selection.from")
          rect            (posToDOMRect view from from)
          patched-rect    (js/Object.assign
                           #js {}
                           rect
                           #js {:width  (max 1 (.-width rect))
                                :height (max 1 (.-height rect))
                                :right  (+ (.-left rect) (max 1 (.-width rect)))
                                :bottom (+ (.-top rect) (max 1 (.-height rect)))})
          virtual-element #js {:getBoundingClientRect (fn [] patched-rect)}]
      (.then
       (computePosition virtual-element element
                        #js {:placement  "bottom-start"
                             :strategy   "absolute"
                             :middleware #js [(shift) (flip)]})
       (fn [result]
         (let [{:keys [x y strategy]} (js->clj result :keywordize-keys true)]
           (when (and x y strategy)
             (dg/set-style! element "width" "max-content")
             (dg/set-style! element "position" strategy)
             (dg/set-style! element "left" (str x "px"))
             (dg/set-style! element "top" (str y "px")))))))
    (catch js/Error e
      (js/console.error "Failed to compute mention position:" e))))

(defn mention-suggestion
  [{:keys [trigger-char item-texts]}]
  (let [lc-items (map cu/str-trim-lcase item-texts)]
    (-> {:char          trigger-char
         :startOfLine   false
         :supportSpaces false
         :match         (fn [text]
                          (let [escaped-trigger (js/RegExp.escape trigger-char)
                                pattern         (js/RegExp. (str "(^|[\\s\\(\\[\\{,])" escaped-trigger "([\\w]*)$"))
                                match           (.match text pattern)]
                            (when match
                              (let [prefix (aget match 1)
                                    query  (aget match 2)
                                    from   (+ (.-index match) (.-length prefix))
                                    to     (.-length text)]
                                #js {:range #js {:from from :to to}
                                     :query query
                                     :text  (aget match 0)}))))
         :items         (fn [args-js]
                          (let [{:keys [query]} (js->clj args-js :keywordize-keys true)
                                lc-query        (cu/str-trim-lcase query)]
                            (->> lc-items
                                 (filter #(string/starts-with? % lc-query))
                                 (vec)
                                 (clj->js))))
         :render        (fn []
                          (let [renderer (atom nil)]
                            #js {:onStart   (fn [props]
                                              (when (oget props "clientRect")
                                                (reset! renderer (create-renderer mention-list props))
                                                (let [el (.-element @renderer)]
                                                  (set-mention-style! el)
                                                  (dg/append-to-body el))
                                                (js/requestAnimationFrame
                                                 (fn []
                                                   (update-position (oget props "editor")
                                                                    (.-element @renderer))))))

                                 :onUpdate  (fn [props]
                                              (when @renderer
                                                (.updateProps @renderer props)
                                                (when (oget props "clientRect")
                                                  (js/requestAnimationFrame
                                                   (fn []
                                                     (update-position (oget props "editor")
                                                                      (.-element @renderer)))))))

                                 :onKeyDown (fn [props]
                                              (if (= (oget props "event.key") "Escape")
                                                (do (.destroy @renderer)
                                                    (dg/remove-element (.-element @renderer))
                                                    true)
                                                (when-let [ref (.-ref @renderer)]
                                                  (when (.-onKeyDown ref)
                                                    (.onKeyDown ref props)))))

                                 :onExit    (fn []
                                              (when @renderer
                                                (.destroy @renderer)
                                                (dg/remove-element (.-element @renderer))))}))}
        (clj->js))))
